import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, Download, CheckCircle2,
  AlertTriangle, XCircle, Loader2, ShoppingCart
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Map Fields", "Audit & Import"];

export default function PurchaseImporterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [parseData, setParseData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/import-purchases/sample-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "bizflowa_purchase_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };

  const processFile = async (file) => {
    if (!file?.name.endsWith(".csv")) { toast.error("Upload a .csv file"); return; }
    try {
      const text = await file.text();
      const r = await api.post("/import-purchases/parse-csv", { csv_content: text });
      setParseData({ ...r.data, csv_content: text });
      setMapping(r.data.suggested_mapping || {});
      setStep(1);
      toast.success(`Parsed ${r.data.total_rows} rows`);
    } catch (err) { toast.error(err.response?.data?.detail || "Parse failed"); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const r = await api.post("/import-purchases/audit", { csv_content: parseData.csv_content, mapping });
      setAuditResult(r.data);
      setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || "Audit failed"); }
    finally { setAuditing(false); }
  };

  const commitImport = async () => {
    if (!auditResult) return;
    setCommitting(true);
    try {
      const validRows = auditResult.rows.filter((r) => r.valid).map((r) => r.data);
      const r = await api.post("/import-purchases/commit", { rows: validRows });
      setCommitResult(r.data);
      toast.success(`Imported ${r.data.imported_count} purchases`);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setCommitting(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="purchase-importer-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/purchases")} data-testid="back-to-purchases"><ArrowLeft className="h-4 w-4 mr-1" /> Purchases</Button>
          <h1 className="font-outfit text-xl font-bold text-[#0f172a]">Import Purchases</h1>
        </div>
        <div className="flex items-center gap-1">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center">
              <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${i < step ? "bg-[#10b981]/10 text-[#10b981]" : i === step ? "bg-[#4338ca] text-white" : "bg-[#f1f5f9] text-[#94a3b8]"}`}>
                {i < step ? <CheckCircle2 className="h-3 w-3 inline mr-1" /> : null}{label}
              </div>
              {i < STEPS.length - 1 && <div className={`w-4 h-px mx-0.5 ${i < step ? "bg-[#10b981]" : "bg-[#e2e8f0]"}`} />}
            </div>
          ))}
        </div>
      </div>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-6">
          {step === 0 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div><h2 className="text-lg font-semibold">Upload Purchase Data</h2><p className="text-sm text-[#64748b]">Import purchase records (COGS / direct costs)</p></div>
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-purchase-template" className="text-[#4338ca]"><Download className="h-3.5 w-3.5 mr-1.5" /> Sample Template</Button>
              </div>
              <div className="relative border-2 border-dashed rounded-2xl p-12 text-center border-[#e2e8f0] hover:border-[#cbd5e1] bg-[#f8fafc] cursor-pointer" data-testid="purchase-csv-drop-zone">
                <input type="file" accept=".csv" onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ""; }} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="purchase-csv-file-input" />
                <div className="flex flex-col items-center gap-3">
                  <div className="h-14 w-14 rounded-2xl bg-[#4338ca]/10 flex items-center justify-center"><Upload className="h-6 w-6 text-[#4338ca]" /></div>
                  <p className="text-sm font-medium text-[#0f172a]">Drop CSV here, or click to browse</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["Supplier Name", "Date", "Category", "Description", "Amount", "GST Number", "GST Rate", "CGST", "SGST", "IGST", "Paid Via", "Payment Status", "Reverse Charge", "ITC Eligible"].map((c) => (
                  <span key={c} className="px-2 py-1 bg-white border border-[#e2e8f0] rounded-md text-[11px] text-[#64748b] font-mono">{c}</span>
                ))}
              </div>
            </div>
          )}

          {step === 1 && parseData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div><h2 className="text-lg font-semibold">Preview</h2><p className="text-sm text-[#64748b]">First {parseData.preview.length} of {parseData.total_rows} rows</p></div>
                <Badge className="bg-[#4338ca]/10 text-[#4338ca]"><FileSpreadsheet className="h-3 w-3 mr-1" /> {parseData.total_rows} rows</Badge>
              </div>
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#f8fafc] border-b">{parseData.headers.map((h, i) => <th key={i} className="py-2 px-3 text-left text-[#64748b]">{h}</th>)}</tr></thead>
                  <tbody>{parseData.preview.map((row, i) => <tr key={i} className="border-b last:border-0">{row.map((c, j) => <td key={j} className="py-2 px-3">{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button><Button onClick={() => setStep(2)} className="bg-[#4338ca]"><ArrowRight className="h-4 w-4 mr-1" /> Map Fields</Button></div>
            </div>
          )}

          {step === 2 && parseData && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Map CSV Columns to Purchase Fields</h2>
              <div className="grid gap-3">
                {parseData.bizflowa_fields.map((field) => (
                  <div key={field} className="flex items-center gap-4 p-3 bg-[#f8fafc] rounded-lg">
                    <span className="text-sm font-medium text-[#0f172a] w-32">{field}</span>
                    <ArrowRight className="h-4 w-4 text-[#94a3b8]" />
                    <Select value={mapping[field] || "unmapped"} onValueChange={(v) => setMapping({ ...mapping, [field]: v === "unmapped" ? "" : v })}>
                      <SelectTrigger className="flex-1 h-9"><SelectValue placeholder="Select column" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unmapped">-- Skip --</SelectItem>
                        {parseData.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {mapping[field] && <CheckCircle2 className="h-4 w-4 text-[#10b981]" />}
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
                <Button onClick={runAudit} disabled={auditing} className="bg-[#4338ca]">{auditing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Audit & Import</Button>
              </div>
            </div>
          )}

          {step === 3 && auditResult && !commitResult && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Audit Results</h2>
              <div className="grid grid-cols-2 gap-3">
                <Card className="border-[#10b981]/20 bg-[#10b981]/5"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-[#10b981]">{auditResult.valid_count}</p><p className="text-xs text-[#64748b]">Valid</p></CardContent></Card>
                <Card className="border-[#ef4444]/20 bg-[#ef4444]/5"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-[#ef4444]">{auditResult.invalid_count}</p><p className="text-xs text-[#64748b]">Errors</p></CardContent></Card>
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-xl">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#f8fafc] border-b sticky top-0"><th className="py-2 px-3 text-left">#</th><th className="py-2 px-3 text-left">Supplier</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3 text-left">Category</th><th className="py-2 px-3 text-center">Status</th></tr></thead>
                  <tbody>{auditResult.rows.map((r) => (
                    <tr key={r.row} className="border-b last:border-0">
                      <td className="py-2 px-3 text-[#94a3b8]">{r.row}</td>
                      <td className="py-2 px-3 font-medium">{r.data.vendor_name}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatINR(r.data.amount)}</td>
                      <td className="py-2 px-3 text-[#64748b]">{r.data.category || "-"}</td>
                      <td className="py-2 px-3 text-center">
                        {r.valid ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981] inline" /> : <XCircle className="h-3.5 w-3.5 text-[#ef4444] inline" />}
                        {r.warnings?.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-[#f59e0b] inline ml-1" />}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
                <Button onClick={commitImport} disabled={committing || auditResult.valid_count === 0} data-testid="commit-purchase-import" className="bg-[#4338ca]">
                  {committing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShoppingCart className="h-4 w-4 mr-1" />}
                  Import {auditResult.valid_count} Purchase{auditResult.valid_count !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          )}

          {commitResult && (
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-[#10b981]/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-[#10b981]" /></div>
              <h2 className="text-xl font-bold text-[#0f172a]">Import Complete!</h2>
              <p className="text-sm text-[#64748b]">Imported {commitResult.imported_count} purchases{commitResult.skipped_count > 0 ? `, ${commitResult.skipped_count} skipped` : ""}</p>
              <Button onClick={() => navigate("/purchases")} className="bg-[#4338ca]">Go to Purchases</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
